import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLimitSwapPair } from '~/state/dex/hooks'
import { useTokenPrice } from '~/hooks/useTokenPrice'
import BigNumber from 'bignumber.js'
import styled from 'styled-components'
import { useEstimatedFastSellPrice } from '../../hooks/useEstimatedExecutionPrice'
import Tooltip from '~/components/Tooltip'
import { useModalOpened } from '~/state/application/hooks'
import { useLimitOrderUserConfig } from '~/state/dex/hooks'
import { useAppDispatch } from '~/state/hooks'
import { updateLimitOrderUserConfig } from '~/state/dex/actions'
import { ApplicationModal } from '~/state/application/reducer'
import { useSmallOrderVerify } from '../../hooks/useTradeVerify'
import { useUSDValueSpreadCallback } from '../SpreadLabel'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'
import { useBreakpoint } from '~/hooks'
import { useWeb3React } from '@web3-react/core'

const FastSellPriceWrapper: React.FC = () => {
  const { t } = useTranslation()
  const { isMobile } = useBreakpoint()
  const dispatch = useAppDispatch()
  const { account } = useWeb3React()

  const [inputToken, outputToken] = useLimitSwapPair()
  const inputPrice = useTokenPrice(inputToken?.address)
  const outputPrice = useTokenPrice(outputToken?.address)
  const isSmallOrder = useSmallOrderVerify()
  const [isLowPrice, setIsLowPrice] = useState(false)

  const isPreviewOpen = useModalOpened(ApplicationModal.LIMIT_ORDER_PREVIEW)

  const calcUsdValueSpread = useUSDValueSpreadCallback()

  const { isInputRateDirection } = useLimitOrderUserConfig()
  const leftToken = isInputRateDirection ? inputToken : outputToken
  const rightToken = isInputRateDirection ? outputToken : inputToken
  const fastSellRate = useEstimatedFastSellPrice()

  const reversedFastSellRate = useMemo(() => {
    const isValid = Number(fastSellRate) > 0
    if (!isValid) return '0'
    const rate = new BigNumber(1).div(fastSellRate).toFixed(6)
    return Number(rate) ? rate : '~'
  }, [fastSellRate])
  const displayFastSellRate = isInputRateDirection
    ? fastSellRate
    : reversedFastSellRate
  const isCorrectFastSellRate = Number(displayFastSellRate) > 0

  const fastSellRateUSDLiteral = useMemo(() => {
    const price = isInputRateDirection ? outputPrice : inputPrice
    const usd = new BigNumber(displayFastSellRate)
      .multipliedBy(price)
      .toFixed(2)
    return isMobile ? '' : Number(usd) ? `(~$${usd})` : '(~)'
  }, [
    isInputRateDirection,
    isMobile,
    displayFastSellRate,
    inputPrice,
    outputPrice,
  ])

  const hideFastSellPrice =
    isPreviewOpen ||
    isLowPrice ||
    isSmallOrder ||
    !isCorrectFastSellRate ||
    !account

  const convert = () => {
    dispatch(
      updateLimitOrderUserConfig({
        isInputRateDirection: !isInputRateDirection,
      }),
    )
  }

  useEffect(() => {
    const usdValueSpread = calcUsdValueSpread()
    const isLowPrice = Number(usdValueSpread) <= -5
    setIsLowPrice(isLowPrice)
  }, [calcUsdValueSpread])

  if (hideFastSellPrice) return null

  return (
    <StyledFastSellPriceWrapper>
      <div>
        <span>{t('est_fast_sell_price')}</span>
        <Tooltip
          placement="top"
          title={t('est_fast_sell_price_tip')}
          trigger="hover"
          img={IconQuestionMark}
        />
      </div>
      <div>
        {`1 ${leftToken?.symbol}`}
        <span onClick={convert} style={{ cursor: 'pointer' }}>
          {' ≈ '}
        </span>
        {`${displayFastSellRate} ${rightToken?.symbol} ${fastSellRateUSDLiteral}`}
      </div>
    </StyledFastSellPriceWrapper>
  )
}

export default FastSellPriceWrapper

const StyledFastSellPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  font-weight: 400;
  font-size: 13px;
  line-height: 140%;
  color: #2c2842;
`
